import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flame } from "lucide-react";
import { scrollToId } from "../../lib/api";
import { useDiary } from "../../lib/diary";
import { computeStreak } from "../../lib/irrigation";

const MILESTONES = [3, 5, 10, 15, 20, 30, 50];
const cheer = (n) => (n >= 20 ? "¡Leyenda de la selva!" : n >= 10 ? "¡Imparable!" : n >= 5 ? "¡Qué ritmo!" : n >= 3 ? "¡Buena racha!" : "¡Sigue así!");

export const useStreak = (cfg) => {
  const [rows] = useDiary();
  const done = useMemo(() => new Set(rows.filter((r) => r.source === "riego").map((r) => r.date)), [rows]);
  return useMemo(() => computeStreak(cfg, done), [cfg, done]);
};

// Hero pill (tablet/desktop): consecutive waterings without forgetting any, celebrated by the monkey.
// On phones the count shows as a badge on the watering icon so the toucan stays visible.
export const JStreak = ({ cfg }) => {
  const n = useStreak(cfg);
  const party = MILESTONES.includes(n);
  return (
    <AnimatePresence>
      {n > 0 && (
        <motion.button key="streak" onClick={() => scrollToId("registro-riegos")} data-testid="streak-button" data-streak={n}
          aria-label={`Racha de ${n} riegos seguidos sin olvidar ninguno`}
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: 0.4 }}
          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          className="pointer-events-auto relative hidden sm:inline-flex items-center gap-1.5 h-14 rounded-full bg-[#0E2A1C]/80 backdrop-blur-md border border-[#8CD05A]/70 pl-0.5 pr-3.5 shadow-[0_12px_30px_-10px_rgba(0,0,0,0.7)]">
          <motion.img src="/img/streak-monkey.webp" alt="El mono celebra tu racha" className="w-[52px] h-[52px] object-contain"
            animate={party ? { y: [0, -10, 0, -6, 0], rotate: [0, -10, 10, -6, 0] } : { y: [0, -4, 0], rotate: [0, -4, 0] }}
            transition={{ duration: party ? 1.2 : 2.2, repeat: Infinity, repeatDelay: party ? 0.6 : 1.4, ease: "easeInOut" }} style={{ transformOrigin: "50% 90%" }} />
          <span className="text-left leading-tight">
            <span className="flex items-center gap-1 font-mono-d text-[9px] tracking-[0.16em] uppercase text-[#8CD05A]"><Flame className="w-3 h-3" /> Racha</span>
            <span data-testid="streak-count" className="block text-xs font-bold text-[#F6EEDC]">{n} {n === 1 ? "riego" : "riegos"} · {cheer(n)}</span>
          </span>
        </motion.button>
      )}
    </AnimatePresence>
  );
};
